import { StreamManifest } from 'content/library/types/contentVideoType';
import { VideoThumbnailType } from './videoThumbnailType';

interface ContentType {
  active: boolean;
  age_rating: string;
  content_type: string;
  duration: number | null;
  episode_order: number | null;
  episode_part: number | null;
  id: number;
  release_date_end: string | null;
  release_date_start: string | null;
  season_id: number | null;
  slug: string;
  status: string;
  synopsis: string | null;
  synopsis_en: string | null;
  title: string;
  title_en: string | null;
}

export interface VideoHistory {
  id: number;
  video: number;
  user: string;
  action: string;
  status: string;
  extra_info: Record<string, any>;
  created?: string; // 생성 시간 (선택적 필드로 추가)
}

export interface MediaholdFileEntry {
  preset: string;
  s3_path: string;
  filename: string;
  filesize: number;
}

export interface MediaholdFiles {
  original_files: Record<string, MediaholdFileEntry>;
  main_resolution?: string;
}

export interface SampleVideo {
  id: number;
  preset: string;
  file_url: string;
  status: string;
}

export interface VideoType {
  id: number;
  created?: string;
  file_name: string;
  file_size?: number;
  file_type?: string;
  file?: string;
  external_url?: string;
  file_url?: string;
  stream_manifests?: StreamManifest[];
  video_histories?: VideoHistory[];
  video_thumbnails?: VideoThumbnailType[];
  sample_videos?: SampleVideo[];
  mediahold_files?: MediaholdFiles;
  ingest_source?: string;
  duration?: number;
  width?: number | null;
  height?: number | null;
  status?: string;
  stream_manifest_status?: string;
  contents?: ContentType[];
}

/**
 * 브라우저 <video> 가 직접 재생할 수 있는 컨테이너 확장자.
 * react-player FilePlayer 의 VIDEO_EXTENSIONS + HLS(hls.js 경유) 와 맞춘다.
 * .ts/.m2ts/.mkv/.avi/.mxf 같은 원본은 트랜스코드는 되지만 네이티브 재생이 안 된다.
 */
const BROWSER_PLAYABLE_EXTENSIONS = new Set(['mp4', 'm4v', 'webm', 'ogg', 'ogv', 'mov', 'm3u8']);

const getUrlExtension = (url: string): string => {
  const path = url.split(/[?#]/)[0];
  const fileName = path.substring(path.lastIndexOf('/') + 1);
  const dot = fileName.lastIndexOf('.');
  return dot >= 0 ? fileName.substring(dot + 1).toLowerCase() : '';
};

/**
 * URL 이 브라우저에서 직접 재생 가능한 컨테이너인지.
 * 확장자를 알 수 없는 URL 은 기존 동작 유지를 위해 재생 가능으로 간주한다.
 */
export const isBrowserPlayableUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  const ext = getUrlExtension(url);
  return ext === '' || BROWSER_PLAYABLE_EXTENSIONS.has(ext);
};

/** video.js sources[].type 용 MIME. m3u8 이면 VHS 가 HLS 로 인식하도록 지정한다. */
export const getVideoSourceType = (url: string | null | undefined): string =>
  url && getUrlExtension(url) === 'm3u8' ? 'application/x-mpegURL' : 'video/mp4';

const getCompletedDirectUrl = (video: VideoType): string | undefined => {
  if (video.status?.toLowerCase() !== 'completed') return undefined;
  return video.file_url || video.external_url || undefined;
};

/** 완료된 HLS 매니페스트 URL (1080p 우선, 다음 720p). 없으면 undefined. */
export const getPlayableHlsUrl = (video: VideoType | null | undefined): string | undefined => {
  const manifests = video?.stream_manifests ?? [];
  for (const preset of ['1080p', '720p']) {
    const manifest = manifests.find(
      m => m.preset === preset && m.status?.toLowerCase() === 'completed'
    );
    if (manifest?.stream_url_full) return manifest.stream_url_full;
  }
  return undefined;
};

/**
 * Check if a video asset has a valid playable video URL.
 * Returns true if:
 * 1. Video status is "completed" AND (file_url or external_url exists), OR
 * 2. stream_manifests has an entry with preset "1080p" or "720p" AND status "completed"
 */
export const hasPlayableVideoUrl = (video: VideoType | null | undefined): boolean => {
  if (!video) return false;
  return Boolean(getCompletedDirectUrl(video) || getPlayableHlsUrl(video));
};

/**
 * Get the best playable video URL from a video asset.
 * Priority:
 * 1. file_url / external_url (status completed) — 브라우저가 직접 재생 가능한 컨테이너일 때만
 * 2. stream_manifests 1080p, then 720p (status completed)
 * 3. file_url / external_url (status completed) — HLS 가 없으면 재생 불가 컨테이너라도 마지막 수단
 */
export const getPlayableVideoUrl = (video: VideoType | null | undefined): string | undefined => {
  if (!video) return undefined;

  const directUrl = getCompletedDirectUrl(video);
  if (directUrl && isBrowserPlayableUrl(directUrl)) return directUrl;

  return getPlayableHlsUrl(video) ?? directUrl;
};
